import ServiceHookResult from "utilities/interfaces/service-hook-result";
import { useCallback } from "react";
import OfflineBooksService from "utilities/services/offline/offline-books-service";
import OfflineBookRecord from "models/view-models/offline/offline-book-record";
import { useQuery } from "react-query";
import { RecordUtils, ResultRecord } from "@rsm-hcd/javascript-core";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface UseOfflineBooksOptions {
    offlineDeviceId?: number;
}

interface UseOfflineBooksHook
    extends ServiceHookResult<Array<OfflineBookRecord>> {
    refresh: () => Promise<void>;
}

// #endregion Interfaces

const OFFLINE_BOOKS_KEY = "offline-books";

// -----------------------------------------------------------------------------------------
// #region Hook
// -----------------------------------------------------------------------------------------

export default function useOfflineBooks(
    options: UseOfflineBooksOptions
): UseOfflineBooksHook {
    const { offlineDeviceId } = options;

    const { list: listOfflineBooks } = OfflineBooksService.useList();

    const query = useQuery(
        [OFFLINE_BOOKS_KEY, offlineDeviceId],
        async () => {
            if (offlineDeviceId == null) return [];

            const { resultObjects: offlineBooks } = await listOfflineBooks(
                {
                    offlineDeviceId,
                },
                {
                    includeBooks: true,
                    includeSyncStatuses: true,
                }
            );

            return offlineBooks;
        },
        {
            enabled: offlineDeviceId != null,
            staleTime: Infinity,
            keepPreviousData: true,
        }
    );

    const refresh = useCallback(async () => {
        await query.refetch();
    }, [query]);

    return {
        resultObject: query.data ?? [],
        rowCount: query.data?.length ?? 0,
        loading: query.isLoading,
        loaded: query.isSuccess,
        errors: query.isError ? getErrors(query.error) : [],
        refresh,
    };
}

// #endregion Hook

const getErrors = (error: any): Array<string> => {
    if (typeof error === "string") {
        return [error];
    }

    if (RecordUtils.isRecord(error, ResultRecord)) {
        return [...error.listErrorMessages()];
    }

    if (error.message != null) {
        return [error.message];
    }

    return [];
};
